import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OverflowTooltip,
  Space,
  Text,
  TextLabel,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GlobalApi } from '@Apis';
import {
  InputChildRef,
  InputWithValid,
} from '../../../../../components/Common/Form/InputWithValid';
import { TextareaWithValid } from '../../../../../components/Common/Form/TextareaWithValid';
import config from '../../../../../config';
import { ValidProps } from '../../../../../types';
import {
  CreateRecipientGroupReqDto,
  RecipientGroupsProps,
} from '../../../../../types/globalSetting';
import common from '../../../../../utils/common';
import { reportPendo } from '../../../../../utils/setupPendo';
import {
  validateIsNotEmptyString,
  validateIsNotWhitespace,
} from '../../../../../utils/validate';
import { onUpdateRecipientGroup } from '../shared';
import { TFunction } from 'i18next';

export async function createRecipientGroup(
  groupName: string,
  recipients: string,
  t: TFunction<'translation', undefined>
) {
  const params: CreateRecipientGroupReqDto = {
    emails: [],
    recipientGroupName: '',
    isDefault: false,
  };
  params.recipientGroupName = groupName;
  params.emails = recipients.split(',').filter((item) => item !== '');
  try {
    const res = await GlobalApi.addRecipientGroups(params);
    if (res.data?.id) {
      window.showNotification({
        titleMsg: t('globalSetting.recipientGroup.add.success'),
        severity: 'success',
      });
    }
  } catch (error: any) {
    const code = error?.status || 'Unknown';
    if (code === 400) {
      window.showNotification({
        titleMsg: t('common.error.invalidFormat'),
        severity: 'error',
      });
    } else {
      window.showNotification({
        titleMsg: t('common.apiException.internalError'),
        severity: 'error',
      });
    }
  }
}

const CustomerRecipientGroupModal = (props: {
  onClose: () => void;
  visible: boolean;
  loadAllRecipientGroups: () => void;
  data?: RecipientGroupsProps | undefined;
  withEditPermission: boolean;
}) => {
  const { t } = useTranslation();
  const [groupName, setGroupName] = useState('');
  const [recipients, setRecipients] = useState('');
  useEffect(() => {
    if (props.data) {
      setGroupName(
        props.data && props.data.recipientGroupName
          ? props.data.recipientGroupName
          : ''
      );
      setRecipients(
        props.data && props.data.emails.length > 0
          ? props.data?.emails.join(',')
          : ''
      );
    }
    console.log('ref');
  }, [props.data]);

  const onSave = async () => {
    await inputChildRef.current?.validate(groupName);
    if (inputChildRef.current?.errorMsg !== undefined) return;

    await textareaChildRef.current?.validate(recipients);
    if (textareaChildRef.current?.errorMsg !== undefined) return;
    props.onClose();
    await createRecipientGroup(groupName, recipients, t);
    await props.loadAllRecipientGroups();
    reportPendo('GlobalSetting.RecipientGroup.AddRecipientGroup', {
      name: groupName,
      number: recipients.split(',').length,
    });
  };

  const onUpdate = async () => {
    await inputChildRef.current?.validate(groupName);
    if (inputChildRef.current?.errorMsg !== undefined) return;

    await textareaChildRef.current?.validate(recipients);
    if (textareaChildRef.current?.errorMsg !== undefined) return;

    props.onClose();
    await onUpdateRecipientGroup(
      (props.data as any).id,
      recipients,
      t,
      groupName
    );
    await props.loadAllRecipientGroups();
    reportPendo('GlobalSetting.RecipientGroup.UpdateRecipientGroup', {
      id: (props.data as any).id,
      type: (props.data as any).type,
      number: recipients.split(',').length,
    });
  };
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const inputChildRef = useRef<InputChildRef>(null);
  const textareaChildRef = useRef<InputChildRef>(null);
  const validates: ValidProps[] = [
    {
      validMethod: validateIsNotWhitespace,
      errMsg: t('globalSetting.recipientGroup.groupNameInput.validate.empty'),
    },
  ];
  const validatesRecipients: ValidProps[] = [
    {
      validMethod: (value: string) => {
        const emails = value.split(',');
        for (const email of emails) {
          if (!common.validateEmail(email)) {
            return false;
          }
        }
        return true;
      },
      errMsg: t(
        'globalSetting.recipientGroup.recipientTextarea.validate.invalidFormat'
      ),
    },
    {
      validMethod: validateIsNotEmptyString,
      errMsg: t(
        'globalSetting.recipientGroup.recipientTextarea.validate.empty'
      ),
    },
  ];

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={props.visible}
      onClose={props.onClose}
      size="sm"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <OverflowTooltip>
            {props.data
              ? t('globalSetting.recipientGroup.title')
              : t('globalSetting.recipientGroup.addRecipient.title')}
          </OverflowTooltip>
        </ModalHeader>
        <ModalBody>
          <Grid templateColumns="min-content auto" columnGap="3x" rowGap="3x">
            <Grid>
              <TextLabel whiteSpace="nowrap">
                {t(
                  'globalSetting.recipientGroup.defaultGroup.label.groupName.title'
                )}
                :<Space color="red:50">*</Space>
              </TextLabel>
            </Grid>
            <Grid>
              <InputWithValid
                ref={inputChildRef}
                onChange={setGroupName}
                value={groupName}
                validates={validates}
                maxLength={
                  config.ui.globalSettings.limitation
                    .recipientGroupInputMaxLength
                }
                placeholder={t(
                  'globalSetting.recipientGroup.defaultGroup.label.groupName.placeholder'
                )}
              />
            </Grid>
            <Grid>
              <TextLabel whiteSpace="nowrap">
                {t(
                  'globalSetting.recipientGroup.defaultGroup.label.recipients.title'
                )}
                :<Space color="red:50">*</Space>
              </TextLabel>
            </Grid>
            <Grid>
              <TextareaWithValid
                ref={textareaChildRef}
                onChange={setRecipients}
                value={recipients}
                validates={validatesRecipients}
                maxLength={
                  config.ui.globalSettings.limitation
                    .recipientGroupTextAreaMaxLength
                }
                placeholder={t(
                  'globalSetting.recipientGroup.defaultGroup.label.recipients.placeholder'
                )}
              />
              <Text color={colorStyle.color.secondary} mt={4}>
                {t('globalSetting.recipientGroup.defaultGroup.textArea.note')}
              </Text>
            </Grid>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="primary"
            minWidth="20x"
            disabled={!props.withEditPermission}
            onClick={!props.data ? onSave : onUpdate}
          >
            {t('common.button.save')}
          </Button>
          <Space width="2x" />
          <Button minWidth="20x" onClick={props.onClose}>
            {t('common.button.cancel')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default React.memo(CustomerRecipientGroupModal);
